/**
 * /login (rev 14 — fixed an infinite redirect loop).
 *
 * BUG INTRODUCED IN rev 13 AND CAUGHT IN THE BROWSER
 *   rev13 made this route `<Navigate to="/" />` UNCONDITIONALLY. In a build
 *   where `VITE_DEMO_MODE` is not set, the demo session is never installed, so:
 *
 *     "/"  → route guard: not authenticated → redirect "/login"
 *     "/login" → redirect "/"
 *     "/"  → …
 *
 *   React Router aborts the cycle, and the page renders EMPTY: a blank screen
 *   with two console errors. It looked like a broken deployment; it was a
 *   routing bug in this file.
 *
 * FIX
 *   The redirect now applies ONLY in a demo build. In a normal build this
 *   route renders a "not yet configured" notice instead of looping and instead
 *   of showing a credential form that could never succeed.
 *
 * THE REAL LOGIN RETURNS LATER
 *   Per-doctor identity with 2FA via Supabase Auth or Cloudflare Access, once
 *   the identity authority (Gate F1) is chosen and `apps/api` is deployed.
 */
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { isDemoMode, installDemoSession, clearDemoSession, isDemoSession as isDemoSessionActive } from "@/lib/demo-mode";

function NotConfiguredNotice() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="card-surface w-full max-w-md rounded-2xl p-8 text-center">
        <h1 className="text-2xl font-bold text-foreground">DIKDOC</h1>
        <p className="mt-2 text-sm text-muted-foreground">Healthcare Operating System</p>
        <div
          className="mt-6 rounded-md border border-amber-500/50 bg-amber-500/10 p-4 text-left text-xs leading-relaxed"
          role="alert"
        >
          <p className="font-semibold text-amber-700 dark:text-amber-400">
            Connexion non configurée
          </p>
          <p className="mt-2 text-muted-foreground">
            L&apos;authentification n&apos;est pas encore branchée. L&apos;API
            n&apos;est pas déployée, donc aucune identification ne peut aboutir.
          </p>
          <p className="mt-2 text-muted-foreground">
            Aucune donnée réelle n&apos;est accessible : l&apos;API n&apos;est pas
            déployée et conserve son contrôle d&apos;accès.
          </p>
        </div>

        {/* rev15 — runtime enable, so UI review no longer depends on a rebuild.
            VITE_* is inlined at BUILD time, which made this a multi-redeploy
            trap. This button reaches the same state without touching Cloudflare
            environment variables.
            RISK: identical to building with VITE_DEMO_MODE=1 — a client-side
            session that grants no API access. It is a review convenience, not
            an authentication mechanism. */}
        <button
          type="button"
          onClick={() => {
            installDemoSession();
            window.location.assign("/");
          }}
          className="mt-4 w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Ouvrir en mode démo
        </button>
        {isDemoSessionActive() && (
          <button
            type="button"
            onClick={() => {
              clearDemoSession();
              window.location.reload();
            }}
            className="mt-2 w-full rounded-md border border-input px-4 py-2 text-xs text-muted-foreground"
          >
            Quitter le mode démo
          </button>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/login/")({
  component: function LoginPage() {
    // Demo build: enter the app. Normal build: explain, never loop.
    if (isDemoMode() || isDemoSessionActive()) return <Navigate to="/" />;
    return <NotConfiguredNotice />;
  },
});
